import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {from, Observable, of, startWith, switchMap} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {AsyncPipe, NgClass} from '@angular/common';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {ContactService} from '../../services/contact.service';
import {CSService} from '../../services/cs.service';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {LaService} from '../../services/la.service';

export interface RelatedEntity {
  name: string,
  icon: string,
  id: string,
  type: string,
  deleted?: boolean
}

@Component({
  selector: 'app-related-contact-cs-la',
  templateUrl: 'relate-contact-cs.component.html',
  styleUrl: 'relate-contact-cs.component.scss',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    AsyncPipe,
    MatIcon,
    NgClass,
    MatTooltip,
  ],
  standalone: true
})
export class RelateContactCsComponent implements OnInit {
  @Input() selected?: string;
  @Input() size?: 'small' | 'medium' = 'small';
  @Input() editable: boolean = true;
  @Output() selectedChange = new EventEmitter<RelatedEntity|null>();

  control = new FormControl<RelatedEntity| string |null>(null);
  selectedEntity: RelatedEntity | null = null;
  filtered: Observable<RelatedEntity[]>;
  nameToLong = false;
  tooltipText = 'Deletado';

  isEditing = signal(false);
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  @ViewChild('textContainer', { static: false }) textContainer!: ElementRef;

  constructor(
    private contactService: ContactService,
    private cstService: CSService,
    private laService: LaService
  ) {
    this.filtered = this.control.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap(state => {
        const term = (state && typeof state === 'string') ? state : '';
        if (!term) return of([]);
        return from(this.searchEntities(term));
      }),
    );

    this.control.valueChanges.subscribe((value) => {
      if(typeof value !== 'string') {
        this.selectedEntity = value;
        this.selectedChange.emit(value);
        this.isEditing.set(false);
      }
    })
  }

  ngOnInit() {
    if (this.selected) {
      this.resolveSelectedEntity(this.selected);
    }
  }

  private async searchEntities(term: string): Promise<RelatedEntity[]> {
    const [contacts, csList, laList] = await Promise.all([
      this.contactService.searchContacts(term, 10),
      this.cstService.searchCS(term, 10),
      this.laService.searchLA(term, 10),
    ]);
    const entities: RelatedEntity[] = [];
    contacts.filter(c => !c.deletedAt).forEach(c =>
      entities.push({ name: c.name, icon: c.type === 'PF' ? 'person' : 'business', id: c.id, type: 'contacts', deleted: false })
    );
    csList.filter(cs => !cs.deletedAt).forEach(cs =>
      entities.push({ name: cs.subject, icon: 'support_agent', id: cs.id, type: 'cs', deleted: false })
    );
    laList.filter(la => !la.deletedAt).forEach(la => {
      const name = la.number ? `${la.number} - ${la.subject}` : la.subject;
      entities.push({ name, icon: 'gavel', id: la.id, type: 'la', deleted: false });
    });
    return entities;
  }

  private async resolveSelectedEntity(id: string) {
    try {
      const contact = await this.contactService.getContactById(id);
      if (contact) {
        const entity: RelatedEntity = { name: contact.name, icon: contact.type === 'PF' ? 'person' : 'business',
          id: contact.id, type: 'contacts', deleted: !!contact.deletedAt };
        this.control.setValue(entity, { emitEvent: false });
        this.selectedEntity = entity;
        this.checkTextOverflow();
        return;
      }
    } catch {}
    try {
      const cs = await this.cstService.getCSById(id);
      if (cs) {
        const entity: RelatedEntity = { name: cs.subject, icon: 'support_agent', id: cs.id, type: 'cs', deleted: !!cs.deletedAt };
        this.control.setValue(entity, { emitEvent: false });
        this.selectedEntity = entity;
        this.checkTextOverflow();
        return;
      }
    } catch {}
    try {
      const la = await this.laService.getLAById(id);
      if (la) {
        const name = la.number ? `${la.number} - ${la.subject}` : la.subject;
        const entity: RelatedEntity = { name, icon: 'gavel', id: la.id, type: 'la', deleted: !!la.deletedAt };
        this.control.setValue(entity, { emitEvent: false });
        this.selectedEntity = entity;
        this.checkTextOverflow();
      }
    } catch {}
  }

  displayFn(entity: RelatedEntity){
    return entity ? entity.name : '';
  }

  clear(){
    this.control.setValue(null);
  }

  edit(){
    if(this.editable){
      this.isEditing.set(true);
      setTimeout(() => {
        this.input.nativeElement.focus();
      },100)
    }
  }

  checkTextOverflow(): void {
    if(!this.textContainer || !this.selectedEntity) return;
    const element = this.textContainer.nativeElement;
    this.nameToLong = element.scrollWidth > element.offsetWidth;
    this.tooltipText = '';
    if (this.nameToLong) {
      this.tooltipText += this.selectedEntity.name
      if (this.selectedEntity.deleted) {
        this.tooltipText += ' - Deletado';
      }
    } else {
      this.tooltipText = 'Deletado';
    }
  }
  openEntity(){
    if (this.selectedEntity) {
      if (this.selectedEntity.type === 'contacts') {
        window.open('/contatos/' + this.selectedEntity.id)
      } else if (this.selectedEntity.type === 'cs') {
        window.open('/atendimentos/' + this.selectedEntity.id)
      } else if (this.selectedEntity.type === 'la') {
        window.open('/acoes-legislativas/' + this.selectedEntity.id)
      }
    }
  }
}
